import type { HTMLAttributes, Ref } from 'react';
import { HeadingContext } from 'react-aria-components/Heading';
import { Provider } from 'react-aria-components/slots';
import { TextContext } from 'react-aria-components/Text';
import type { VariantProps } from 'class-variance-authority';
import { cva, cx } from 'class-variance-authority';

import { ButtonContext } from '../Button';
import { ButtonGroupContext } from '../ButtonGroup';

import type { EmptyStateIllustration } from './illustrations';
import { emptyStateDarkIllustrations, emptyStateIllustrations } from './illustrations';

import styles from './EmptyState.module.css';

const emptyStateStyles = cva(styles.base, {
	variants: {
		size: {
			large: styles.large,
		},
		hasBorder: {
			true: styles.bordered,
			false: null,
		},
	},
	defaultVariants: {
		size: 'large',
		hasBorder: false,
	},
});

const Illustration = ({ illustration }: { illustration: EmptyStateIllustration }) => {
	const dark = emptyStateDarkIllustrations[illustration];

	return (
		<>
			<img
				src={emptyStateIllustrations[illustration]}
				alt=""
				className={cx(styles.illustration, dark && styles.lightOnly)}
			/>
			{dark && <img src={dark} alt="" className={cx(styles.illustration, styles.darkOnly)} />}
		</>
	);
};

interface EmptyStateVariants extends VariantProps<typeof emptyStateStyles> {}

interface EmptyStateProps extends HTMLAttributes<HTMLDivElement>, EmptyStateVariants {
	ref?: Ref<HTMLDivElement>;
	/**
	 * A spot illustration rendered above the heading. Decorative, so it is hidden from assistive
	 * technology. Replaces the `BadgeIcon` child; don't pass both.
	 */
	illustration?: EmptyStateIllustration;
}

/**
 * An empty state displays an illustration and a message, usually when there is no content to show.
 *
 * Follows the React Spectrum IllustratedMessage composition pattern: an illustration, Heading, Text,
 * and optional Button (action). Set the illustration with the `illustration` prop; a `BadgeIcon`
 * child is still supported while existing empty states migrate.
 *
 * https://react-spectrum.adobe.com/v3/IllustratedMessage.html
 */
const EmptyState = ({
	className,
	children,
	size = 'large',
	hasBorder = false,
	illustration,
	ref,
	...props
}: EmptyStateProps) => {
	return (
		<div ref={ref} {...props} className={emptyStateStyles({ size, hasBorder, className })}>
			{illustration && <Illustration illustration={illustration} />}
			<Provider
				values={[
					[HeadingContext, { className: styles.heading }],
					[TextContext, { className: styles.description }],
					[
						ButtonContext,
						{
							size: size === 'large' ? 'large' : 'medium',
						},
					],
					[ButtonGroupContext, { className: styles.actions }],
				]}
			>
				{children}
			</Provider>
		</div>
	);
};

export { EmptyState, emptyStateStyles };
export type { EmptyStateProps };
